import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import securityApi from '../api/securityApi';

// Create the context
const ComplianceContext = createContext();

// SOC 2 Type I framework UUID (from backend seed data)
const SOC2_TYPE1_FRAMEWORK_ID = '550e8400-e29b-41d4-a716-446655440001';

// TSC Category options
export const TSC_CATEGORIES = [
  { id: 'Security', label: 'Security', required: true, description: 'Core security controls' },
  { id: 'Availability', label: 'Availability', required: false, description: 'System availability commitments' },
  { id: 'Confidentiality', label: 'Confidentiality', required: false, description: 'Data confidentiality protections' },
  { id: 'Processing Integrity', label: 'Processing Integrity', required: false, description: 'Data processing accuracy' },
  { id: 'Privacy', label: 'Privacy', required: false, description: 'Personal information protection' },
];

// Available frameworks (extensible for future)
export const COMPLIANCE_FRAMEWORKS = [
  { id: SOC2_TYPE1_FRAMEWORK_ID, name: 'SOC 2 Type I', version: '2017', enabled: true },
  { id: 'soc2-type2', name: 'SOC 2 Type II', version: '2017', enabled: false, comingSoon: true },
  { id: 'iso27001', name: 'ISO 27001', version: '2022', enabled: false, comingSoon: true },
];

// Create a provider component
export const ComplianceProvider = ({ children }) => {
  // Wizard navigation state
  const [currentStep, setCurrentStep] = useState(1);
  const [projectId, setProjectId] = useState(null);

  // Step 1 - Project Setup state
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    frameworkId: SOC2_TYPE1_FRAMEWORK_ID,
    tscCategories: ['Security'], // Security is required
    targetAuditDate: null,
  });

  // Step 2 - Integrations state
  const [integrations, setIntegrations] = useState({
    aws: { connected: false, status: 'disconnected', integrationId: null, name: null },
    google_workspace: { connected: false, status: 'disconnected', integrationId: null, name: null },
    github: { connected: false, status: 'disconnected', integrationId: null, name: null },
  });

  // GitHub App info state
  const [githubAppInfo, setGithubAppInfo] = useState({
    configured: false,
    appName: null,
    installationUrl: null,
    installations: [],
    loading: false,
    error: null,
  });

  // AWS SSO flow state
  const [awsSSOState, setAWSSSOState] = useState({
    state: null,
    verificationUrl: null,
    verificationUrlComplete: null,
    userCode: null,
    expiresIn: null,
    interval: 5,
    status: null,
    message: null,
    isPolling: false, // Flag to trigger polling via useEffect
    // Account/role selection state
    integrationId: null,
    showRoleSelection: false,
    accounts: [],
    accountsLoading: false,
    accountsError: null,
    selectedAccount: null,
    selectedRole: null,
  });

  // Step 3 - Scan & Gap Analysis state
  const [scanSession, setScanSession] = useState({
    sessionId: null,
    initiatedScans: [],
    status: 'idle', // 'idle', 'scanning', 'complete', 'error'
    progress: 0,
    error: null,
  });
  const [gapAnalysis, setGapAnalysis] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update project data (for form fields)
  const updateProjectData = useCallback((updates) => {
    setProjectData(prev => ({ ...prev, ...updates }));
  }, []);

  // Toggle TSC category selection
  const toggleTSCCategory = useCallback((categoryId) => {
    setProjectData(prev => {
      const current = prev.tscCategories;
      if (categoryId === 'Security') return prev; // Security is required, can't toggle

      const newCategories = current.includes(categoryId)
        ? current.filter(c => c !== categoryId)
        : [...current, categoryId];

      return { ...prev, tscCategories: newCategories };
    });
  }, []);

  // Step 1: Create the compliance project
  const createProject = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Format the date for API
      const formattedDate = projectData.targetAuditDate
        ? projectData.targetAuditDate.toISOString().split('T')[0]
        : null;

      const response = await securityApi.createComplianceProject({
        ...projectData,
        targetAuditDate: formattedDate,
      });

      setProjectId(response.id);
      setCurrentStep(2);
      return response;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create project');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectData]);

  // Step 2: Fetch integration status
  const fetchIntegrationStatus = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await securityApi.getProjectIntegrations(projectId);

      // Use functional update to avoid stale closure and infinite loops
      setIntegrations(prevIntegrations => {
        const newIntegrations = { ...prevIntegrations };
        response.integrations.forEach(integration => {
          const key = integration.type === 'google_workspace' ? 'google_workspace' : integration.type;
          if (newIntegrations[key]) {
            newIntegrations[key] = {
              connected: integration.connected,
              status: integration.connected ? 'connected' : 'disconnected',
              integrationId: integration.integration_id,
              name: integration.integration_name,
              lastScanAt: integration.last_scan_at,
            };
          }
        });
        return newIntegrations;
      });

      return response.integrations;
    } catch (err) {
      console.error('Error fetching integration status:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Connect AWS integration (legacy - redirects to access key flow)
  const connectAWS = useCallback(() => {
    const returnUrl = `${window.location.origin}/compliance/new?step=2&project_id=${projectId}&integration=aws`;
    securityApi.initiateAWSConnection(returnUrl);
  }, [projectId]);

  // Connect AWS via SSO (IAM Identity Center)
  const connectAWSSSO = useCallback(async (config) => {
    const { name, ssoStartUrl, ssoRegion } = config;

    // Update status to connecting
    setIntegrations(prev => ({
      ...prev,
      aws: { ...prev.aws, status: 'connecting' },
    }));

    try {
      const returnUrl = `${window.location.origin}/compliance/new?step=2&project_id=${projectId}&integration=aws`;
      const response = await securityApi.initiateAWSSSOConnection(
        name,
        ssoStartUrl,
        ssoRegion,
        returnUrl
      );

      // Store SSO flow state and trigger polling
      setAWSSSOState({
        state: response.state,
        verificationUrl: response.verification_uri,
        verificationUrlComplete: response.verification_uri_complete,
        userCode: response.user_code,
        expiresIn: response.expires_in,
        interval: response.interval || 5,
        status: 'pending',
        message: 'Waiting for authentication...',
        isPolling: true, // Trigger polling via useEffect
      });

      return response;
    } catch (err) {
      setIntegrations(prev => ({
        ...prev,
        aws: { ...prev.aws, status: 'error' },
      }));
      setAWSSSOState(prev => ({
        ...prev,
        status: 'error',
        message: err.response?.data?.detail || err.message || 'Failed to initiate SSO',
      }));
      throw err;
    }
  }, [projectId]);

  // useEffect to manage AWS SSO polling with automatic cleanup
  useEffect(() => {
    // Only start polling if isPolling flag is true and we have a state
    if (!awsSSOState.isPolling || !awsSSOState.state) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const result = await securityApi.pollAWSSSOStatus(awsSSOState.state);

        setAWSSSOState(prev => ({
          ...prev,
          status: result.status,
          message: result.message,
        }));

        if (result.status === 'complete') {
          // Authentication successful - now show account/role selection
          // Stop polling by setting isPolling to false
          setAWSSSOState(prev => ({
            ...prev,
            isPolling: false,
            integrationId: result.integration_id,
            showRoleSelection: true,
            accountsLoading: true,
            status: 'selecting_role',
            message: 'Loading AWS accounts...',
          }));

          // Update integration to "selecting_role" status
          setIntegrations(prev => ({
            ...prev,
            aws: {
              ...prev.aws,
              status: 'selecting_role',
              integrationId: result.integration_id,
            },
          }));

          // Fetch available AWS accounts
          try {
            const accountsResponse = await securityApi.listAWSSSOAccounts(result.integration_id);
            setAWSSSOState(prev => ({
              ...prev,
              accounts: accountsResponse.accounts || [],
              accountsLoading: false,
              message: 'Select an AWS account and role',
            }));
          } catch (err) {
            console.error('Error fetching AWS accounts:', err);
            setAWSSSOState(prev => ({
              ...prev,
              accountsLoading: false,
              accountsError: err.message || 'Failed to load AWS accounts',
              message: 'Failed to load accounts',
            }));
          }
        } else if (result.status === 'expired' || result.status === 'error') {
          // Authentication failed or expired - stop polling
          setAWSSSOState(prev => ({
            ...prev,
            isPolling: false,
          }));
          setIntegrations(prev => ({
            ...prev,
            aws: { ...prev.aws, status: 'error' },
          }));
        }
        // For 'pending' or 'polling', continue polling
      } catch (err) {
        console.error('Error polling AWS SSO status:', err);
        // Don't stop polling on transient errors
      }
    }, awsSSOState.interval * 1000);

    // Cleanup function - automatically called on unmount or when dependencies change
    return () => {
      clearInterval(pollInterval);
    };
  }, [awsSSOState.isPolling, awsSSOState.state, awsSSOState.interval]);

  // Cancel AWS SSO flow
  const cancelAWSSSO = useCallback(async () => {
    // Cancel on server if we have a state
    if (awsSSOState.state) {
      try {
        await securityApi.cancelAWSSSOFlow(awsSSOState.state);
      } catch (err) {
        console.error('Error cancelling AWS SSO flow:', err);
      }
    }

    // Reset state (setting isPolling to false will trigger cleanup in useEffect)
    setAWSSSOState({
      state: null,
      verificationUrl: null,
      verificationUrlComplete: null,
      userCode: null,
      expiresIn: null,
      interval: 5,
      status: null,
      message: null,
      isPolling: false,
      integrationId: null,
      showRoleSelection: false,
      accounts: [],
      accountsLoading: false,
      accountsError: null,
      selectedAccount: null,
      selectedRole: null,
    });

    setIntegrations(prev => ({
      ...prev,
      aws: { ...prev.aws, status: 'disconnected' },
    }));
  }, [awsSSOState.state]);

  // Select AWS account and role for SSO integration
  const selectAWSSSORole = useCallback(async (accountId, roleName) => {
    if (!awsSSOState.integrationId) {
      console.error('No integration ID for role selection');
      return;
    }

    setAWSSSOState(prev => ({
      ...prev,
      status: 'selecting_role',
      message: 'Connecting to AWS account...',
    }));

    try {
      await securityApi.selectAWSSSORole(awsSSOState.integrationId, accountId, roleName);

      // Success - integration is now fully connected
      setIntegrations(prev => ({
        ...prev,
        aws: {
          connected: true,
          status: 'connected',
          integrationId: awsSSOState.integrationId,
          name: prev.aws.name,
        },
      }));

      // Reset SSO state
      setAWSSSOState({
        state: null,
        verificationUrl: null,
        verificationUrlComplete: null,
        userCode: null,
        expiresIn: null,
        interval: 5,
        status: null,
        message: null,
        isPolling: false,
        integrationId: null,
        showRoleSelection: false,
        accounts: [],
        accountsLoading: false,
        accountsError: null,
        selectedAccount: null,
        selectedRole: null,
      });

      // Refresh integration status
      fetchIntegrationStatus();
    } catch (err) {
      console.error('Error selecting AWS role:', err);
      setAWSSSOState(prev => ({
        ...prev,
        status: 'error',
        message: err.response?.data?.detail || err.message || 'Failed to connect to AWS account',
      }));
    }
  }, [awsSSOState.integrationId, fetchIntegrationStatus]);

  // Configure AWS role for existing SSO integration
  const configureAWSRole = useCallback(async (integrationId) => {
    if (!integrationId) {
      integrationId = integrations.aws.integrationId;
    }

    if (!integrationId) {
      console.error('No AWS integration ID available');
      return;
    }

    // Show role selection modal and load accounts
    setAWSSSOState(prev => ({
      ...prev,
      integrationId: integrationId,
      showRoleSelection: true,
      accountsLoading: true,
      accountsError: null,
      status: 'selecting_role',
      message: 'Loading AWS accounts...',
    }));

    try {
      const accountsResponse = await securityApi.listAWSSSOAccounts(integrationId);
      setAWSSSOState(prev => ({
        ...prev,
        accounts: accountsResponse.accounts || [],
        accountsLoading: false,
        message: 'Select an AWS account and role',
      }));
    } catch (err) {
      console.error('Error fetching AWS accounts:', err);
      setAWSSSOState(prev => ({
        ...prev,
        accountsLoading: false,
        accountsError: err.response?.data?.detail || err.message || 'Failed to load AWS accounts',
        message: 'Failed to load accounts',
      }));
    }
  }, [integrations.aws.integrationId]);

  // Connect Google Workspace integration
  const connectGoogleWorkspace = useCallback(() => {
    const returnUrl = `${window.location.origin}/compliance/new?step=2&project_id=${projectId}&integration=gws`;
    securityApi.initiateGWSConnection(returnUrl);
  }, [projectId]);

  // Fetch GitHub App info
  const fetchGitHubAppInfo = useCallback(async () => {
    setGithubAppInfo(prev => ({ ...prev, loading: true, error: null }));
    try {
      const appInfo = await securityApi.getGithubAppInfo();

      let installations = [];
      if (appInfo && appInfo.app_id) {
        try {
          const installationsResponse = await securityApi.getGithubAppInstallations();
          installations = installationsResponse.installations || [];
        } catch (err) {
          console.error('Error loading GitHub App installations:', err);
        }
      }

      setGithubAppInfo({
        configured: !!(appInfo && appInfo.app_id),
        appName: appInfo?.app_name || null,
        installationUrl: appInfo?.installation_url || null,
        installations,
        loading: false,
        error: null,
      });

      return appInfo;
    } catch (err) {
      console.error('Error fetching GitHub App info:', err);
      setGithubAppInfo(prev => ({
        ...prev,
        configured: false,
        loading: false,
        error: err.message || 'Failed to fetch GitHub App info',
      }));
      return null;
    }
  }, []);

  // Connect GitHub integration via GitHub App installation
  const connectGitHub = useCallback(() => {
    if (githubAppInfo.installationUrl) {
      // Open GitHub App installation page in a new tab
      window.open(githubAppInfo.installationUrl, '_blank');
    } else {
      // If we don't have the URL yet, try to fetch it first
      fetchGitHubAppInfo().then(appInfo => {
        if (appInfo?.installation_url) {
          window.open(appInfo.installation_url, '_blank');
        } else {
          console.error('GitHub App is not configured');
        }
      });
    }
  }, [githubAppInfo.installationUrl, fetchGitHubAppInfo]);

  // Remove a GitHub App installation
  const removeGithubInstallation = useCallback(async (installationId) => {
    try {
      await securityApi.deleteGithubAppInstallation(installationId);
    } catch (err) {
      console.error('Error removing GitHub installation:', err);
      return { success: false, error: err.response?.data?.detail || err.message || 'Failed to remove installation' };
    }
    // Refresh state independently — failures here don't undo the deletion
    await Promise.allSettled([fetchGitHubAppInfo(), fetchIntegrationStatus()]);
    return { success: true };
  }, [fetchGitHubAppInfo, fetchIntegrationStatus]);

  // Proceed to Step 3
  const proceedToScan = useCallback(() => {
    setCurrentStep(3);
  }, []);

  // Poll for gap analysis updates
  const pollGapAnalysis = useCallback(() => {
    if (!projectId) return () => {};

    const pollInterval = setInterval(async () => {
      try {
        const analysis = await securityApi.getGapAnalysis(projectId);
        setGapAnalysis(analysis);

        // Update progress based on analysis
        // Note: progress_percentage is COMPLIANCE progress (controls verified / total)
        // scan_status indicates if scans are still running
        const progress = analysis.progress_percentage || 0;
        setScanSession(prev => ({
          ...prev,
          progress,
        }));

        // Check if all scans are complete (not compliance progress, but scan execution)
        // scan_status: 'running' means scans still in progress, 'complete' means all done
        if (analysis.scan_status === 'complete') {
          clearInterval(pollInterval);
          setScanSession(prev => ({
            ...prev,
            status: 'complete',
          }));
        }
      } catch (err) {
        console.error('Error polling gap analysis:', err);
        // Don't clear interval on error, keep trying
      }
    }, 3000); // Poll every 3 seconds

    // Store interval ID for cleanup
    return () => clearInterval(pollInterval);
  }, [projectId]);

  // Step 3: Trigger initial scan
  const triggerScan = useCallback(async () => {
    if (!projectId) return;

    setScanSession(prev => ({
      ...prev,
      status: 'scanning',
      progress: 0,
      error: null,
    }));

    try {
      const response = await securityApi.triggerInitialScan(projectId);

      setScanSession(prev => ({
        ...prev,
        sessionId: response.session_id,
        initiatedScans: response.initiated_scans || [],
      }));

      // Note: Polling is managed by the component via useEffect
      // to ensure proper cleanup on unmount

      return response;
    } catch (err) {
      setScanSession(prev => ({
        ...prev,
        status: 'error',
        error: err.response?.data?.detail || err.message || 'Failed to start scan',
      }));
      throw err;
    }
  }, [projectId]);

  // Reset wizard state
  const resetWizard = useCallback(() => {
    setCurrentStep(1);
    setProjectId(null);
    setProjectData({
      name: '',
      description: '',
      frameworkId: SOC2_TYPE1_FRAMEWORK_ID,
      tscCategories: ['Security'],
      targetAuditDate: null,
    });
    setIntegrations({
      aws: { connected: false, status: 'disconnected', integrationId: null, name: null },
      google_workspace: { connected: false, status: 'disconnected', integrationId: null, name: null },
      github: { connected: false, status: 'disconnected', integrationId: null, name: null },
    });
    // Reset SSO state (setting isPolling to false will trigger cleanup in useEffect)
    setAWSSSOState({
      state: null,
      verificationUrl: null,
      verificationUrlComplete: null,
      userCode: null,
      expiresIn: null,
      interval: 5,
      status: null,
      message: null,
      isPolling: false,
      integrationId: null,
      showRoleSelection: false,
      accounts: [],
      accountsLoading: false,
      accountsError: null,
      selectedAccount: null,
      selectedRole: null,
    });
    setScanSession({
      sessionId: null,
      initiatedScans: [],
      status: 'idle',
      progress: 0,
      error: null,
    });
    setGapAnalysis(null);
    setLoading(false);
    setError(null);
  }, []);

  // Restore wizard state from URL params (for OAuth callback)
  const restoreFromParams = useCallback((searchParams) => {
    const step = parseInt(searchParams.get('step')) || 1;
    const projectIdParam = searchParams.get('project_id');

    if (projectIdParam) {
      setProjectId(projectIdParam);
      setCurrentStep(step);
    }
  }, []);

  // Go back to previous step
  const goBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Context value
  const value = {
    // State
    currentStep,
    projectId,
    projectData,
    integrations,
    scanSession,
    gapAnalysis,
    loading,
    error,

    // AWS SSO state
    awsSSOState,

    // GitHub App state
    githubAppInfo,

    // Constants
    TSC_CATEGORIES,
    COMPLIANCE_FRAMEWORKS,

    // Actions
    setCurrentStep,
    updateProjectData,
    toggleTSCCategory,
    createProject,
    fetchIntegrationStatus,
    fetchGitHubAppInfo,
    connectAWS,
    connectAWSSSO,
    cancelAWSSSO,
    selectAWSSSORole,
    configureAWSRole,
    connectGoogleWorkspace,
    connectGitHub,
    removeGithubInstallation,
    proceedToScan,
    triggerScan,
    pollGapAnalysis,
    resetWizard,
    restoreFromParams,
    goBack,
    setError,
  };

  return (
    <ComplianceContext.Provider value={value}>
      {children}
    </ComplianceContext.Provider>
  );
};

// Custom hook to use the compliance context
export const useCompliance = () => {
  const context = useContext(ComplianceContext);
  if (!context) {
    throw new Error('useCompliance must be used within a ComplianceProvider');
  }
  return context;
};

export default ComplianceContext;
